import { WarningIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import { useMemo, useState } from 'react'
import { useToastService } from '../../services/toast-service.tsx'
import { useDeleteRepositoryProjectMemberships } from '../../utils/api-client/api-hooks.ts'
import type { Project, Repository } from '../../utils/api-client/api-schemas.ts'
import type { AvailableTemplate } from '../../utils/api-client/templates.ts'
import { pluralize } from '../../utils/text-utils.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { useShowProjectMutationError } from '../projects/admin-required-override.ts'
import { outlinedGhost } from '../projects/project-dialog.css.ts'
import { SCMIcon } from '../third-party-branding/scm-icon.tsx'
import * as styles from './confirm-dialog.css.ts'

/** How many names a selection lists before it falls back to "+N more". */
const NAMES_SHOWN = 3

export type RemovableRepository = Pick<Repository, 'id' | 'full_name' | 'type'> & {
  membershipDelete: AvailableTemplate
}

export type RemoveRepositoryModalProps = {
  /** The repositories leaving the project — one row's action, or the whole selection. Never empty. */
  repositories: RemovableRepository[]
  project: Pick<Project, 'id' | 'name'>
  onClose: () => unknown
  onRemoved: () => unknown
  /**
   * Returns focus to whatever opened this dialog. Radix restores to a `Dialog.Trigger`, and this
   * dialog has none — it mounts already open. @see useReturnFocus
   */
  onCloseAutoFocus?: (event: Event) => void
}

/** Confirms taking repositories out of their project. Not destructive: they stay connected. */
export const RemoveRepositoryModal: React.FC<RemoveRepositoryModalProps> = ({
  repositories,
  project,
  onClose,
  onRemoved,
  onCloseAutoFocus,
}) => {
  const { addToast } = useToastService()
  // What the dialog is still asking about. It starts as the whole selection and narrows to whatever a
  // partly-failed attempt left behind, so the list on screen never claims rows that are already gone.
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(
    () => new Set(repositories.map(repository => repository.id))
  )
  const subjects = useMemo(
    () => repositories.filter(repository => pendingIds.has(repository.id)),
    [repositories, pendingIds]
  )
  const count = subjects.length
  const single = count === 1 ? subjects[0] : undefined

  const showError = useShowProjectMutationError<typeof useDeleteRepositoryProjectMemberships>(
    count === 1 ? 'Failed to remove repository' : 'Failed to remove repositories'
  )
  const removeMutation = useDeleteRepositoryProjectMemberships({
    onSuccess: ({ removed, failed }, { removals }) => {
      if (failed.length === 0) {
        addToast({
          message: (
            <>
              {removals.length} {pluralize(removals.length, 'repository', 'repositories')} removed from &ldquo;
              {project.name}&rdquo;
            </>
          ),
          variant: 'success',
        })
        onRemoved()
        return
      }

      // A mixed result is the one case neither toast describes. Say what went through, and leave the
      // dialog asking about only what did not, so a retry is not a re-send of the whole selection.
      addToast({
        message: (
          <>
            Removed {removed.length} of {removals.length}. {failed.length} failed:{' '}
            {summarizeNames(repositories.filter(repository => failed.includes(repository.id)))}
          </>
        ),
        variant: 'warning',
      })
      setPendingIds(new Set(failed))
    },
    onError: showError,
  })
  const isPending = removeMutation.isPending

  const handleRemove = () =>
    removeMutation.mutate({
      removals: subjects.map(repository => ({ repositoryId: repository.id, template: repository.membershipDelete })),
    })

  return (
    <Dialog.Root
      open
      onOpenChange={open => {
        if (!open && !isPending) {
          onClose()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          className={styles.content}
          onPointerDownOutside={event => event.preventDefault()}
          onCloseAutoFocus={onCloseAutoFocus}
        >
          <Dialog.Title className={styles.title}>
            <WarningIcon size={32} weight="fill" className={styles.cautionIcon} aria-hidden />
            {single ? 'Remove repository?' : `Remove ${count} repositories?`}
          </Dialog.Title>
          <div className={styles.subjectCard}>
            {single ? (
              <>
                <span className={styles.subjectKind}>
                  <SCMIcon type={single.type} size="medium" />
                  <span>Repository</span>
                </span>
                <p className={styles.subjectName}>{single.full_name}</p>
              </>
            ) : (
              <>
                <span className={styles.subjectKind}>
                  <span>
                    {count} {pluralize(count, 'repository', 'repositories')}
                  </span>
                </span>
                <p className={styles.subjectName}>{summarizeNames(subjects)}</p>
              </>
            )}
          </div>
          <Dialog.Description asChild>
            <p className={styles.description}>
              {single ? 'It stays' : 'They stay'} connected to Pixee but will no longer receive this project&apos;s
              feedback. You can move {single ? 'it' : 'them'} back at any time.
            </p>
          </Dialog.Description>
          <div className={styles.buttons}>
            <GhostButton
              type="secondary"
              size="small"
              className={outlinedGhost}
              onClick={onClose}
              state={isPending ? 'disabled' : 'default'}
            >
              Cancel
            </GhostButton>
            <DefaultButton
              buttonType="secondary"
              size="small"
              state={isPending ? 'loading' : 'default'}
              onClick={handleRemove}
              loadingLabel={single ? 'Removing repository' : 'Removing repositories'}
            >
              {single ? 'Remove repository' : `Remove ${count} repositories`}
            </DefaultButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

const summarizeNames = (repositories: Pick<Repository, 'full_name'>[]) => {
  const shown = repositories.slice(0, NAMES_SHOWN).map(repository => repository.full_name)
  const rest = repositories.length - shown.length
  return rest > 0 ? `${shown.join(', ')} +${rest} more` : shown.join(', ')
}
